import React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Button,
	Typography,
	Grid,
	Box,
	Alert,
	FormControl,
	Select,
	MenuItem,
} from "@mui/material";
import Appbar from "../appbar/Appbar";
import Sidebar from "../sidebar/Sidebar";
import { useAppContext } from "../../context/AppContext";
import { useLocation } from "react-router-dom";
import services from "../../services/services";
import userServices from "../../services/userServices";
import styles from "./downloadpage.module.scss";
import CircularProgress from "@mui/material/CircularProgress";
import uploadPageImg from "../../assets/uploadpageimg.svg";
import LoadingSpinner from "../../Utils/Loader/LoadingSpinner";
import ErrorCountsTable from "./error-counts-table/ErrorCountsTable";
import FeedbackModal from "../FeedbackModal/FeedbackModal";

export default function DownloadFile(props) {
	const {
		features: { download_as_csv },
		loadingFeatures,
	} = useAppContext();

	const { state } = useLocation();
	const [loader, setLoader] = useState(true);
	const [downloading, setDownloading] = useState(false);
	const [fileFormat, setFileFormat] = useState("xlsx");
	const [fileProcessResp, setFileprocess] = useState(null);
	const [responseError, setResponseError] = useState({
		status: "",
		message: "",
	});
	const [submitting, setSubmitting] = useState(false);
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [emailStatus, setEmailStatus] = useState(null);
	const [showFeedback, setShowFeedback] = useState(false);
	const [submissionData, setSubmissionData] = useState(null);
	
	const navigate = useNavigate();

	useEffect(() => {
		fetchData();
	}, []);

	const handleFinalSubmit = () => {
		setSubmitting(true);
		setResponseError({ status: "", message: "" });
		setEmailStatus(null);
		
		const fileId = fileProcessResp?.id;
		const filename = fileProcessResp?.filename;
		
		// Simple validation
		if (!fileId || !filename) {
			setResponseError({ 
				status: "error", 
				message: "File information is missing. Cannot submit." 
			});
			setSubmitting(false);
			return;
		}
		
		// Backend will get vendor/email from req.user
		const submissionDataPayload = {
			fileId: fileId,
			vendorName: "placeholder",
			submitterEmail: "placeholder@example.com",
			filename: filename,
			originalFilename: fileProcessResp?.original_filename || null,
			submitterName: null
		};
		
		services.submitFileWithEmails(submissionDataPayload)
			.then((response) => {
				setSubmitting(false);
				setSubmitSuccess(true);
				
				if (response.emailResults) {
					const { confirmation, notification } = response.emailResults;
					if (confirmation && notification) {
						setEmailStatus({
							type: "success",
							message: "File submitted successfully! Confirmation and notification emails have been sent."
						});
						
						setSubmissionData({
							fileId: fileId,
							vendorName: "Vendor",
							submitterEmail: "user"
						});
						
						setTimeout(() => {
							setShowFeedback(true);
						}, 1500);
						
					} else if (confirmation || notification) {
						setEmailStatus({
							type: "warning", 
							message: "File submitted successfully! Some emails may have failed to send."
						});
					} else {
						setEmailStatus({
							type: "warning",
							message: "File submitted successfully! However, emails failed to send."
						});
					}
				} else {
					setEmailStatus({
						type: "success",
						message: "File submitted successfully!"
					});
				}
			})
			.catch((err) => {
				setSubmitting(false);
				setResponseError({
					status: "error",
					message: err.response?.data?.detail || err.message || "Failed to submit file. Please try again."
				});
			});
	};

	const fetchData = async () => {
		if (!state?.id) {
			setLoader(false);
			setResponseError({
				status: "error", 
				message: "No file ID provided"
			});
			return;
		}
		
		let encodedID = encodeURIComponent(state.id);
		
		try {
			const response = await services.checkFileProcess(encodedID);
			
			if (response && typeof response === 'object') {
				setLoader(false);
				setFileprocess(response);
				setResponseError({ status: "", message: "" });
			} else {
				setLoader(false);
				setResponseError({
					status: "error",
					message: "Invalid response format from server"
				});
			}
		} catch (err) {
			setLoader(false);
			
			let errorMessage = "Processing took too long. Check back in a while or make sure the uploaded file is in a valid format";
			
			if (err.message) {
				if (err.message.includes("still being processed")) {
					errorMessage = "File is still being processed. Please wait a moment and refresh the page.";
				} else if (err.message.includes("timeout")) {
					errorMessage = "Request timed out. File may still be processing. Please try again.";
				} else if (err.message.includes("Network")) {
					errorMessage = "Network connection issue. Please check your connection and try again.";
				} else {
					errorMessage = err.message;
				}
			}
			
			setResponseError({
				status: "error",
				message: errorMessage
			});
		}
	};

	const handleDownload = (fileFormat) => {
		setDownloading(true);
		services
			.downloadFileReq(fileProcessResp.id, fileProcessResp.filename, fileFormat)
			.then((res) => {
				setDownloading(false);
			})
			.catch((err) => {
				alert(err.message);
				setDownloading(false);
			});
	};

	return (
		<>
			<Grid container>
				{loader ? (
					<Grid
						item
						container
						md={12}
						display={"flex"}
						justifyContent={"center"}
						alignItems={"center"}
					>
						<Grid
							item
							md={4}
							sm={4}
							xs={4}
							flexDirection={"column"}
							display={"flex"}
							justifyContent={"center"}
							alignItems={"center"}
							alignSelf={"center"}
							height={window.innerHeight - 100}
						>
							<div>
								<CircularProgress style={{ color: "#4b286d" }} />
							</div>
							<div>
								<Typography style={{ wordWrap: "break-word" }}>
									The wait is almost over... just kidding, we're still trying to
									figure out how to operate the scanner...
								</Typography>
							</div>
						</Grid>
					</Grid>
				) : responseError.status ? (
					<Grid item md={6} m={"auto"} mt={"5rem"}>
						<Typography>
							{responseError.message}
						</Typography>
					</Grid>
				) : (
					<Grid
						item
						md={12}
						mt={"3rem"}
						display={"flex"}
						alignItems={"center"}
						flexDirection={"column"}
					>
						{responseError.status && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{responseError.message}
							</Alert>
						)}
						
						{submitSuccess && emailStatus && (
							<Alert 
								severity={emailStatus.type} 
								sx={{ mb: 2, mt: 2, maxWidth: '600px' }}
							>
								{emailStatus.message}
							</Alert>
						)}
						
						{submitting && (
							<Alert severity="info" sx={{ mb: 2 }}>
								Submitting file and sending emails...
							</Alert>
						)}
						
						{downloading && <LoadingSpinner></LoadingSpinner>}
						
						<img
							src={uploadPageImg}
							alt="uploadPageImg"
							width="15%"
							height="auto"
						/>
						<Typography className={styles.h_oneText}>
							Thank you for uploading your spreadsheet!
						</Typography>
						<Typography className={styles.h2Text}>
							If your file does not have any corrections to be made, no need to
							re-upload.{" "}
						</Typography>
						<Typography className={styles.h2Text}>
							If your file requires correction (cells in red), please make the
							appropriate updates and re-upload.
						</Typography>
						<Typography className={styles.h3Text}>
							Click on download to get the corrected excel file and view
							uncorrected data
						</Typography>
						<Box>
							<Button
								className={[styles.buttoncls, styles.bgtransparent].join(" ")}
								onClick={() => {
									navigate("/");
								}}
							>
								Upload New
							</Button>

							{!loadingFeatures &&
							download_as_csv.is_enabled &&
							download_as_csv.roles_allowed.includes(
								sessionStorage.getItem("user_role")
							) ? (
								<FormControl
									variant="standard"
									className={styles.btnformcontrol}
									sx={{ m: 1, minWidth: 150 }}
									size="small"
								>
									<Select
										labelId="demo-select-small-label"
										id="demo-select-small"
										value="downl"
										label="File Format"
										className={styles.btndropdown}
										disableUnderline="true"
										onChange={(e) => {
											if (e.target.value) {
												setFileFormat(e.target.value);
												handleDownload(e.target.value);
											}
										}}
									>
										<MenuItem value={"downl"} style={{ display: "none" }}>
											Download
										</MenuItem>
										<MenuItem value={"xlsx"}>Download as .xlsx</MenuItem>
										<MenuItem value={"csv"}>Download as .csv</MenuItem>
									</Select>
								</FormControl>
							) : (
								<Button
									className={styles.buttoncls}
									onClick={() => {
										handleDownload();
									}}
								>
									Download
								</Button> 
							)}
							
							<Button
								className={styles.buttoncls}
								disabled={
									Number(fileProcessResp?.corrections?.manual_correction_required) !== 0 || 
									submitting || 
									submitSuccess
								}
								onClick={handleFinalSubmit}
								style={{
									opacity: Number(fileProcessResp?.corrections?.manual_correction_required) !== 0 || 
											submitting || 
											submitSuccess ? 0.5 : 1
								}}
							>
								{submitting ? (
									<>
										<CircularProgress size={16} style={{ color: "white", marginRight: "8px" }} />
										Submitting & Sending Emails...
									</>
								) : submitSuccess ? (
									"Submitted & Emails Sent"
								) : (
									"Final Submit"
								)}
							</Button>
						</Box>
						<ErrorCountsTable counts={fileProcessResp.corrections} />
					</Grid>
				)}
			</Grid>			
			<FeedbackModal
				open={showFeedback}
				onClose={() => setShowFeedback(false)}
				fileId={submissionData?.fileId}
				vendorName={submissionData?.vendorName}
				submitterEmail={submissionData?.submitterEmail}
			/>
		</>
	);
}
